import React from 'react';
import { handleCTAAction } from '../../utils/ctaHandlers';

const MessageCTAs = ({ ctas, conversationId, isInHjelpestonadMode = false }) => {
  // Hide ALL CTAs during hjelpestønad flow - conversation IS the interface
  if (isInHjelpestonadMode) {
    return null;
  }

  if (!ctas || ctas.length === 0) {
    return null;
  }

  // Defensive guard: Ensure all CTAs have a type field
  const sanitizedCTAs = ctas.map((cta) => {
    if (!cta.type) {
      console.warn('[MessageCTAs] CTA missing type field, defaulting to "question":', cta);
      return { ...cta, type: 'question' };
    }
    return cta;
  });

  return (
    <div className="mt-4 pt-4 border-t border-border-light">
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
        {sanitizedCTAs.map((cta) => (
          <button
            key={cta.id}
            onClick={() => handleCTAAction(cta, conversationId)}
            className={`
              px-5 py-3 rounded-xl font-medium text-sm transition-all duration-200
              inline-flex items-center gap-3 hover:shadow-lg active:transform active:scale-95
              ${getCtaButtonStyle(cta)}
            `}
          >
            {getCtaIcon(cta)}
            <span className="text-left">{cta.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Get button style based on CTA type
 * Design tokens:
 * - question: sage outline
 * - application: sage filled
 * - external: neutral/gray with external indicator
 * - resource: peach/primary accent
 */
const getCtaButtonStyle = (cta) => {
  // Question CTAs: Sage green outline
  const questionStyle = 'bg-white text-sage-600 border-2 border-sage-500 hover:bg-sage-500 hover:text-white';

  // Application CTAs: Sage green filled
  const applicationStyle = 'bg-sage-500 text-white border-0 shadow-sm hover:bg-sage-600';

  // External CTAs: Neutral gray with subtle styling
  const externalStyle = 'bg-neutral-100 text-text-primary border border-neutral-300 hover:bg-neutral-200';

  // Resource CTAs: Peach/primary accent
  const resourceStyle = 'bg-primary-100 text-primary-700 border border-primary-300 hover:bg-primary-200';

  // Legacy action support
  if (cta.action === 'START_APPLICATION' || cta.action === 'start_hjelpestonad_conversational') {
    return applicationStyle;
  }
  if (cta.action === 'CONTINUE_CHAT') {
    return questionStyle;
  }

  // Type-based styling
  switch (cta.type) {
    case 'application':
      return applicationStyle;
    case 'question':
      return questionStyle;
    case 'external':
      return externalStyle;
    case 'resource':
      return resourceStyle;
    default:
      return questionStyle;
  }
};

/**
 * Get icon based on CTA type
 */
const getCtaIcon = (cta) => {
  // Legacy action support
  if (cta.action === 'START_APPLICATION' || cta.action === 'start_hjelpestonad_conversational') {
    return <DocumentIcon />;
  }
  if (cta.action === 'CONTINUE_CHAT') {
    return <ChatIcon />;
  }

  // Type-based icons
  switch (cta.type) {
    case 'application':
      return <DocumentIcon />;
    case 'question':
      return <ChatIcon />;
    case 'external':
      return <ExternalLinkIcon />;
    case 'resource':
      // Different icon for pdf vs video
      return cta.resourceType === 'video' ? <PlayIcon /> : <DownloadIcon />;
    default:
      return <ChatIcon />;
  }
};

// Icon components
const ChatIcon = () => (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const DocumentIcon = () => (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const PlayIcon = () => (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export default MessageCTAs;
